<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Title & Closing Information
          </h1>
          <p class="text-gray-600 mt-1">Update title information, trustee, and settlement agent for the closing documents.</p>
        </div>
        <router-link to="/dashboard" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Back
        </router-link>
      </div>

      <!-- Title Info Form -->
      <div class="card">
        <div class="card-header flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Vesting & Legal Description</h3>
          <span v-if="isSaved" class="status-badge status-completed">
            <font-awesome-icon icon="check" class="mr-1" />
            Saved
          </span>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveTitleInfo" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Vesting Type -->
              <div>
                <label for="estate" class="form-label">Vesting Type (Estate)</label>
                <select
                  id="estate"
                  v-model="form.estate"
                  class="form-select"
                >
                  <option value="">Select vesting type...</option>
                  <option value="Fee Simple">Fee Simple</option>
                  <option value="Life Estate">Life Estate</option>
                  <option value="Leasehold">Leasehold</option>
                </select>
              </div>

              <!-- Living Trust -->
              <div>
                <label for="livingTrust" class="form-label">Living Trust</label>
                <select
                  id="livingTrust"
                  v-model="form.livingTrust"
                  class="form-select"
                >
                  <option value="None">None</option>
                  <option value="Inter Vivos (Living) Trust">Inter Vivos (Living) Trust</option>
                  <option value="Irrevocable Trust">Irrevocable Trust</option>
                  <option value="Revocable Trust">Revocable Trust</option>
                </select>
              </div>
            </div>

            <!-- Legal Description -->
            <div>
              <label for="legalDescription" class="form-label">Full Legal Description</label>
              <textarea
                id="legalDescription"
                v-model="form.legalDescription"
                class="form-textarea"
                rows="5"
                placeholder="Enter the full legal description of the property..."
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                Include lot, block, subdivision, and recording information as applicable.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Brief Legal Description -->
              <div>
                <label for="briefLegalDescription" class="form-label">Brief Legal Description</label>
                <input
                  id="briefLegalDescription"
                  v-model="form.briefLegalDescription"
                  type="text"
                  class="form-input"
                  placeholder="e.g., Lot 5, Block 2, Sunny Acres Subdivision"
                />
              </div>

              <!-- Assessor Parcel Number -->
              <div>
                <label for="assessorParcelNumber" class="form-label">Assessor Parcel Number (APN)</label>
                <input
                  id="assessorParcelNumber"
                  v-model="form.assessorParcelNumber"
                  type="text"
                  class="form-input"
                  placeholder="e.g., 123-456-789"
                />
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="tolerance-exceeded rounded-lg p-4">
              <div class="flex items-center">
                <font-awesome-icon icon="exclamation-circle" class="text-danger-600 mr-3" />
                <span class="text-sm font-medium text-danger-800">{{ error }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                @click="resetForm"
                class="btn btn-secondary"
                :disabled="isSaving"
              >
                <font-awesome-icon icon="undo" class="mr-2" />
                Reset
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSaving"
              >
                <font-awesome-icon v-if="isSaving" icon="spinner" class="mr-2 animate-spin" />
                <font-awesome-icon v-else icon="save" class="mr-2" />
                {{ isSaving ? 'Saving...' : 'Save Title Info' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Trustee & Settlement Agent Section -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Trustee Selection -->
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-2">
                <font-awesome-icon icon="user-shield" class="text-primary-600 text-sm" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Trustee</h3>
            </div>
            <span v-if="selectedTrustee" class="status-badge status-completed">
              <font-awesome-icon icon="check" class="mr-1" />
              Selected
            </span>
          </div>
          <div class="card-body">
            <p class="text-gray-600 text-sm mb-4">
              Select the trustee for the Deed of Trust from saved trustees or add a new one.
            </p>

            <!-- Selected Trustee Display -->
            <div v-if="selectedTrustee" class="bg-primary-50 rounded-lg p-4 mb-4">
              <div class="flex items-start justify-between">
                <div>
                  <p class="font-semibold text-gray-900">{{ selectedTrustee.name }}</p>
                  <p v-if="selectedTrustee.address" class="text-sm text-gray-600 mt-1">{{ selectedTrustee.address }}</p>
                  <p v-if="selectedTrustee.phone" class="text-sm text-gray-500">{{ selectedTrustee.phone }}</p>
                </div>
                <button @click="clearTrustee" class="text-gray-400 hover:text-gray-600">
                  <font-awesome-icon icon="times" />
                </button>
              </div>
            </div>

            <!-- Trustee Selection -->
            <div v-else>
              <label class="form-label">Select Trustee</label>
              <select v-model="trusteeSelection" class="form-select mb-3">
                <option value="">Choose a saved trustee...</option>
                <option v-for="trustee in savedTrustees" :key="trustee.id" :value="trustee.id">
                  {{ trustee.name }}
                </option>
                <option value="new">+ Add New Trustee</option>
              </select>

              <button
                v-if="trusteeSelection && trusteeSelection !== 'new'"
                @click="selectTrustee"
                class="btn btn-primary w-full"
                :disabled="isSavingTrustee"
              >
                <font-awesome-icon v-if="isSavingTrustee" icon="spinner" class="mr-2 animate-spin" />
                Confirm Selection
              </button>
            </div>

            <div v-if="trusteeError" class="mt-3 tolerance-exceeded rounded-lg p-3">
              <span class="text-sm text-danger-800">{{ trusteeError }}</span>
            </div>
          </div>
        </div>

        <!-- Settlement Agent Selection -->
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-2">
                <font-awesome-icon icon="building" class="text-primary-600 text-sm" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Settlement Agent</h3>
            </div>
            <span v-if="selectedAgent" class="status-badge status-completed">
              <font-awesome-icon icon="check" class="mr-1" />
              Selected
            </span>
          </div>
          <div class="card-body">
            <p class="text-gray-600 text-sm mb-4">
              Select the settlement agent or add yourself as the settlement agent.
            </p>

            <!-- Selected Agent Display -->
            <div v-if="selectedAgent" class="bg-primary-50 rounded-lg p-4 mb-4">
              <div class="flex items-start justify-between">
                <div>
                  <p class="font-semibold text-gray-900">{{ selectedAgent.name }}</p>
                  <p v-if="selectedAgent.address" class="text-sm text-gray-600 mt-1">{{ selectedAgent.address }}</p>
                  <p v-if="selectedAgent.phone" class="text-sm text-gray-500">{{ selectedAgent.phone }}</p>
                  <p v-if="selectedAgent.email" class="text-sm text-gray-500">{{ selectedAgent.email }}</p>
                </div>
                <button @click="clearAgent" class="text-gray-400 hover:text-gray-600">
                  <font-awesome-icon icon="times" />
                </button>
              </div>
            </div>

            <!-- Agent Selection -->
            <div v-else>
              <label class="form-label">Select Settlement Agent</label>
              <select v-model="agentSelection" class="form-select mb-3">
                <option value="">Choose a saved agent...</option>
                <option v-for="agent in savedAgents" :key="agent.id" :value="agent.id">
                  {{ agent.name }}
                </option>
                <option value="self">Use My Company Info</option>
                <option value="new">+ Add New Agent</option>
              </select>

              <button
                v-if="agentSelection && agentSelection !== 'new'"
                @click="selectAgent"
                class="btn btn-primary w-full"
                :disabled="isSavingAgent"
              >
                <font-awesome-icon v-if="isSavingAgent" icon="spinner" class="mr-2 animate-spin" />
                Confirm Selection
              </button>
            </div>

            <div v-if="agentError" class="mt-3 tolerance-exceeded rounded-lg p-3">
              <span class="text-sm text-danger-800">{{ agentError }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <router-link to="/dashboard" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Dashboard
        </router-link>
        <button @click="continueToNext" class="btn btn-primary">
          Continue to Fees
          <font-awesome-icon icon="arrow-right" class="ml-2" />
        </button>
      </div>
    </div>

    <!-- Add Trustee Dialog -->
    <Dialog 
      title="Add New Trustee" 
      :isOpen="isTrusteeDialogOpen" 
      @close="closeTrusteeDialog"
    >
      <form @submit.prevent="saveNewTrustee" class="space-y-4">
        <div>
          <label class="form-label">Trustee Name *</label>
          <input
            v-model="newTrustee.name"
            type="text"
            class="form-input"
            placeholder="e.g., First American Title Company"
            required
          />
        </div>
        <div>
          <label class="form-label">Address</label>
          <input
            v-model="newTrustee.address"
            type="text"
            class="form-input"
            placeholder="123 Main St, City, State ZIP"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="form-label">Phone</label>
            <input
              v-model="newTrustee.phone"
              type="tel"
              class="form-input"
              placeholder="555-123-4567"
            />
          </div>
          <div>
            <label class="form-label">Email</label>
            <input
              v-model="newTrustee.email"
              type="email"
              class="form-input"
              placeholder="email@example.com"
            />
          </div>
        </div>
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <button type="button" @click="closeTrusteeDialog" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isSavingTrustee">
            <font-awesome-icon v-if="isSavingTrustee" icon="spinner" class="mr-2 animate-spin" />
            Add Trustee
          </button>
        </div>
      </form>
    </Dialog>

    <!-- Add Settlement Agent Dialog -->
    <Dialog 
      title="Add New Settlement Agent" 
      :isOpen="isAgentDialogOpen" 
      @close="closeAgentDialog"
    >
      <form @submit.prevent="saveNewAgent" class="space-y-4">
        <div>
          <label class="form-label">Company Name *</label>
          <input
            v-model="newAgent.name"
            type="text"
            class="form-input"
            placeholder="e.g., ABC Title Company"
            required
          />
        </div>
        <div>
          <label class="form-label">Address</label>
          <input
            v-model="newAgent.address"
            type="text"
            class="form-input"
            placeholder="123 Main St, City, State ZIP"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="form-label">Phone</label>
            <input
              v-model="newAgent.phone"
              type="tel"
              class="form-input"
              placeholder="555-123-4567"
            />
          </div>
          <div>
            <label class="form-label">Email</label>
            <input
              v-model="newAgent.email"
              type="email"
              class="form-input"
              placeholder="email@example.com"
            />
          </div>
        </div>
        <div>
          <label class="form-label">Contact Name</label>
          <input
            v-model="newAgent.contactName"
            type="text"
            class="form-input"
            placeholder="John Smith"
          />
        </div>
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <button type="button" @click="closeAgentDialog" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isSavingAgent">
            <font-awesome-icon v-if="isSavingAgent" icon="spinner" class="mr-2 animate-spin" />
            Add Agent
          </button>
        </div>
      </form>
    </Dialog>
  </TitleLayout>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import Dialog from '@/components/Dialog/index.vue'
import apiService from '@/api/apiService'

export default {
  name: 'TitleInfo',
  components: {
    TitleLayout,
    Dialog,
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    // Title Info Form
    const form = reactive({
      estate: '',
      livingTrust: 'None',
      legalDescription: '',
      briefLegalDescription: '',
      assessorParcelNumber: '',
    })

    const originalData = ref(null)
    const isSaving = ref(false)
    const isSaved = ref(false)
    const error = ref(null)

    // Trustee State
    const savedTrustees = ref([])
    const trusteeSelection = ref('')
    const selectedTrustee = ref(null)
    const isTrusteeDialogOpen = ref(false)
    const isSavingTrustee = ref(false)
    const trusteeError = ref(null)
    const newTrustee = reactive({
      name: '',
      address: '',
      phone: '',
      email: '',
    })

    // Settlement Agent State
    const savedAgents = ref([])
    const agentSelection = ref('')
    const selectedAgent = ref(null)
    const isAgentDialogOpen = ref(false)
    const isSavingAgent = ref(false)
    const agentError = ref(null)
    const newAgent = reactive({
      name: '',
      address: '',
      phone: '',
      email: '',
      contactName: '',
    })

    // Watch for "new" selections to open dialogs
    watch(trusteeSelection, (val) => {
      if (val === 'new') {
        isTrusteeDialogOpen.value = true
        trusteeSelection.value = ''
      }
    })

    watch(agentSelection, (val) => {
      if (val === 'new') {
        isAgentDialogOpen.value = true
        agentSelection.value = ''
      }
    })

    const fetchTitleInfo = async () => {
      try {
        store.dispatch('setLoading', { isLoading: true, message: 'Loading title information...' })
        
        const data = await apiService.getTitleInfo()
        console.log('[TitleInfo] Fetched:', data)

        // Populate form - handle both estate_type (backend) and estate (fallback)
        form.estate = data.estate_type || data.estate || ''
        form.livingTrust = data.living_trust || 'None'
        form.legalDescription = data.legal_description || ''
        form.briefLegalDescription = data.brief_legal_description || ''
        form.assessorParcelNumber = data.assessor_parcel_number || ''

        // Set selected trustee/agent if already saved
        if (data.trustee) {
          selectedTrustee.value = data.trustee
        }
        if (data.settlement_agent) {
          selectedAgent.value = data.settlement_agent
        }

        // Store original for reset
        originalData.value = { ...form }

        store.dispatch('setTitleInfo', data)
      } catch (err) {
        console.error('[TitleInfo] Failed to fetch:', err)
        error.value = 'Failed to load title information. Please try again.'
      } finally {
        store.dispatch('setLoading', false)
      }
    }

    const fetchTrustees = async () => {
      try {
        const data = await apiService.getTrustees()
        console.log('[TitleInfo] Trustees fetched:', data)
        
        // Handle different response formats: array, object with trustees property, or single object
        if (Array.isArray(data)) {
          savedTrustees.value = data
        } else if (data.trustees && Array.isArray(data.trustees)) {
          savedTrustees.value = data.trustees
        } else if (data.id) {
          // Single trustee object - wrap in array
          savedTrustees.value = [data]
        } else {
          savedTrustees.value = []
        }
      } catch (err) {
        console.error('[TitleInfo] Failed to fetch trustees:', err)
        savedTrustees.value = []
      }
    }

    const fetchAgents = async () => {
      try {
        const data = await apiService.getSettlementAgents()
        console.log('[TitleInfo] Agents fetched:', data)
        
        // Handle different response formats: array, object with agents property, or single object
        if (Array.isArray(data)) {
          savedAgents.value = data
        } else if (data.agents && Array.isArray(data.agents)) {
          savedAgents.value = data.agents
        } else if (data.settlement_agents && Array.isArray(data.settlement_agents)) {
          savedAgents.value = data.settlement_agents
        } else if (data.id) {
          // Single agent object - wrap in array
          savedAgents.value = [data]
        } else {
          savedAgents.value = []
        }
      } catch (err) {
        console.error('[TitleInfo] Failed to fetch agents:', err)
        savedAgents.value = []
      }
    }

    const saveTitleInfo = async () => {
      isSaving.value = true
      isSaved.value = false
      error.value = null

      try {
        const payload = {
          estate_type: form.estate, // Backend expects estate_type
          living_trust: form.livingTrust,
          legal_description: form.legalDescription,
          brief_legal_description: form.briefLegalDescription,
          assessor_parcel_number: form.assessorParcelNumber,
        }

        const data = await apiService.updateTitleInfo(payload)
        console.log('[TitleInfo] Saved:', data)

        // Update store
        store.dispatch('setTitleInfo', data)
        store.dispatch('setClosingProgress', { titleInfoComplete: true })

        // Store new original
        originalData.value = { ...form }
        isSaved.value = true

        // Clear saved indicator after 3 seconds
        setTimeout(() => {
          isSaved.value = false
        }, 3000)

      } catch (err) {
        console.error('[TitleInfo] Failed to save:', err)
        error.value = err.response?.data?.detail || 'Failed to save title information. Please try again.'
      } finally {
        isSaving.value = false
      }
    }

    const resetForm = () => {
      if (originalData.value) {
        Object.assign(form, originalData.value)
      }
      error.value = null
      isSaved.value = false
    }

    // Trustee Functions
    const selectTrustee = async () => {
      if (!trusteeSelection.value || trusteeSelection.value === 'new') return

      isSavingTrustee.value = true
      trusteeError.value = null

      try {
        const data = await apiService.saveTrustee({ trustee_id: trusteeSelection.value })
        console.log('[TitleInfo] Trustee saved:', data)
        // Handle different response formats
        selectedTrustee.value = data.trustee || data || null
        trusteeSelection.value = ''
      } catch (err) {
        console.error('[TitleInfo] Failed to save trustee:', err)
        trusteeError.value = err.response?.data?.detail || 'Failed to save trustee selection.'
      } finally {
        isSavingTrustee.value = false
      }
    }

    const saveNewTrustee = async () => {
      isSavingTrustee.value = true
      trusteeError.value = null

      try {
        const data = await apiService.saveTrustee({
          name: newTrustee.name,
          address: newTrustee.address,
          phone: newTrustee.phone,
          email: newTrustee.email,
        })
        console.log('[TitleInfo] New trustee saved:', data)
        // Handle different response formats
        selectedTrustee.value = data.trustee || data || null

        // Reset form and close dialog
        newTrustee.name = ''
        newTrustee.address = ''
        newTrustee.phone = ''
        newTrustee.email = ''
        isTrusteeDialogOpen.value = false

        // Refresh trustees list
        await fetchTrustees()
      } catch (err) {
        console.error('[TitleInfo] Failed to save new trustee:', err)
        trusteeError.value = err.response?.data?.detail || 'Failed to add new trustee.'
      } finally {
        isSavingTrustee.value = false
      }
    }

    const clearTrustee = () => {
      selectedTrustee.value = null
      trusteeSelection.value = ''
    }

    const closeTrusteeDialog = () => {
      isTrusteeDialogOpen.value = false
      trusteeSelection.value = ''
    }

    // Settlement Agent Functions
    const selectAgent = async () => {
      if (!agentSelection.value || agentSelection.value === 'new') return

      isSavingAgent.value = true
      agentError.value = null

      try {
        let payload = {}
        
        if (agentSelection.value === 'self') {
          // Use current user's company info
          payload = { use_self: true }
        } else {
          payload = { agent_id: agentSelection.value }
        }

        const data = await apiService.saveSettlementAgent(payload)
        console.log('[TitleInfo] Agent saved:', data)
        // Handle different response formats
        selectedAgent.value = data.agent || data.settlement_agent || data || null
        agentSelection.value = ''
      } catch (err) {
        console.error('[TitleInfo] Failed to save agent:', err)
        agentError.value = err.response?.data?.detail || 'Failed to save settlement agent selection.'
      } finally {
        isSavingAgent.value = false
      }
    }

    const saveNewAgent = async () => {
      isSavingAgent.value = true
      agentError.value = null

      try {
        const data = await apiService.saveSettlementAgent({
          name: newAgent.name,
          address: newAgent.address,
          phone: newAgent.phone,
          email: newAgent.email,
          contact_name: newAgent.contactName,
        })
        console.log('[TitleInfo] New agent saved:', data)
        // Handle different response formats
        selectedAgent.value = data.agent || data.settlement_agent || data || null

        // Reset form and close dialog
        newAgent.name = ''
        newAgent.address = ''
        newAgent.phone = ''
        newAgent.email = ''
        newAgent.contactName = ''
        isAgentDialogOpen.value = false

        // Refresh agents list
        await fetchAgents()
      } catch (err) {
        console.error('[TitleInfo] Failed to save new agent:', err)
        agentError.value = err.response?.data?.detail || 'Failed to add new settlement agent.'
      } finally {
        isSavingAgent.value = false
      }
    }

    const clearAgent = () => {
      selectedAgent.value = null
      agentSelection.value = ''
    }

    const closeAgentDialog = () => {
      isAgentDialogOpen.value = false
      agentSelection.value = ''
    }

    const continueToNext = async () => {
      try {
        // Update closing status via PATCH to mark title info as complete
        await apiService.updateClosingStatus({
          title_info_complete: true,
        })
        
        // Update local store
        store.dispatch('setClosingProgress', { titleInfoComplete: true })
      } catch (err) {
        console.error('[TitleInfo] Failed to update closing status:', err)
        // Still update local store and continue even if update fails
        store.dispatch('setClosingProgress', { titleInfoComplete: true })
      }
      
      router.push('/fees')
    }

    onMounted(() => {
      fetchTitleInfo()
      fetchTrustees()
      fetchAgents()
    })

    return {
      form,
      isSaving,
      isSaved,
      error,
      saveTitleInfo,
      resetForm,
      continueToNext,
      // Trustee
      savedTrustees,
      trusteeSelection,
      selectedTrustee,
      isTrusteeDialogOpen,
      isSavingTrustee,
      trusteeError,
      newTrustee,
      selectTrustee,
      saveNewTrustee,
      clearTrustee,
      closeTrusteeDialog,
      // Settlement Agent
      savedAgents,
      agentSelection,
      selectedAgent,
      isAgentDialogOpen,
      isSavingAgent,
      agentError,
      newAgent,
      selectAgent,
      saveNewAgent,
      clearAgent,
      closeAgentDialog,
    }
  },
}
</script>
